import { useRef, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import { Tooltip } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { useHistory, Link } from 'react-router-dom'
import { CvsButton as Button } from '@components'
import { useHasEllipsis } from '@global/hooks'
import { convertUtcDateStringToLocal } from '@utils/convertUtcDateStringToLocal'
import { Dataset, MoreMenuItems } from 'pages/datasets/types'
import { Favorite } from './favorite'
import { Meta } from './meta'
import { More } from './more'
import * as css from './styles'
import { t } from '@lingui/core/macro'

const DATE_FORMAT = 'YYYY-MM-DD HH:mm'

interface IBulkUploadModalState {
  open: boolean
  templateId: number | null
  requireApproval: boolean
}

interface Props {
  dataset: Dataset | undefined
  moreMenuItems: MoreMenuItems
  shouldShowDescription: boolean
  canViewDatasetDetails: boolean
  setBulkUploadModal: (settings: IBulkUploadModalState) => void
}

export function DatasetCard({
  dataset,
  moreMenuItems,
  canViewDatasetDetails,
  setBulkUploadModal,
  shouldShowDescription,
}: Props) {
  const history = useHistory()

  const [hovered, setHovered] = useState<boolean>(false)

  const titleRef = useRef<HTMLHeadingElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const showTooltip = useHasEllipsis({
    parentElRef: sectionRef,
    childElRef: titleRef,
    offset: 55,
    deps: [dataset?.description, dataset?.updatedAt],
  })

  if (!dataset) return null

  const {
    createdAt,
    description,
    favorite,
    folder,
    id,
    integrations,
    name,
    rowCount,
    updatedAt,
    lastSubmissionTime,
  } = dataset

  const mostRecentTimestamp = lastSubmissionTime ?? updatedAt ?? createdAt
  const shouldRenderDescription = description?.length && shouldShowDescription
  const lastUpdated = convertUtcDateStringToLocal(mostRecentTimestamp, DATE_FORMAT)

  return (
    <css.Section css={hovered && css.hover} ref={cardRef} data-testid="dataset-card">
      <css.Wrapper>
        <css.Card onMouseEnter={enableHover} onMouseLeave={disableHover}>
          <css.ClickableArea
            css={canViewDatasetDetails && css.hoverable}
            onClick={() => {
              viewDataset(id)
            }}
          >
            <css.Header ref={sectionRef}>
              <Tooltip placement="top" title={showTooltip ? name : ''} arrow>
                <css.Title css={hovered && css.hoverHighlight} ref={titleRef}>
                  {name}
                </css.Title>
              </Tooltip>
              <Favorite id={id} favorite={favorite} />
            </css.Header>
            <css.Details>
              <Meta
                folderName={folder?.name}
                integrations={integrations}
                lastUpdated={lastUpdated}
                rowCount={rowCount}
                id={id}
              />
            </css.Details>
          </css.ClickableArea>
          <css.ButtonArea>
            <css.Buttons>
              <Tooltip title={t`New submission`}>
                <Link to={`/codes/create/${id}`}>
                  <Button
                    startIcon={<AddIcon />}
                    data-testid="new-submission"
                    color="primary"
                    variant="contained"
                    size="small"
                    style={{ boxShadow: 'none' }}
                  >
                    {t`New`}
                  </Button>
                </Link>
              </Tooltip>
              <Tooltip title={t`Import file`}>
                <css.ButtonWithIcon
                  aria-haspopup="true"
                  data-testid="import-file"
                  onClick={() => {
                    setBulkUploadModal({
                      open: true,
                      templateId: dataset.settings.templateId,
                      requireApproval: !!dataset.settings.requireApproval,
                    })
                  }}
                >
                  <ImportExportIcon />
                </css.ButtonWithIcon>
              </Tooltip>
              <More datasetId={id} menuItems={moreMenuItems} />
            </css.Buttons>
          </css.ButtonArea>
        </css.Card>
        <AnimatePresence>
          {shouldRenderDescription && (
            <motion.div
              className="card-description"
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={{
                expanded: { height: 'auto', opacity: 1 },
                collapsed: { height: 0, opacity: 0 },
              }}
              transition={{
                duration: 0.3,
                ease: [0.5, 0, 0.5, 1],
              }}
            >
              <css.Description data-testid="dataset-card-description">
                {description}
              </css.Description>
            </motion.div>
          )}
        </AnimatePresence>
      </css.Wrapper>
    </css.Section>
  )

  function enableHover(): void {
    if (!canViewDatasetDetails) return

    setHovered(true)
  }

  function disableHover(): void {
    if (!canViewDatasetDetails) return

    setHovered(false)
  }

  function viewDataset(datasetId: number): void {
    if (!canViewDatasetDetails || !hovered) return

    history.push(`/template/${datasetId}/data`)
  }
}
